import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { styled } from "goober";
import { useEffect, useMemo, useState } from "react";
import { queries } from "../../../../shared/queries";
import type { IconName } from "../../../assets/icons";
import { useQuery } from "../../../hooks/useQuery";
import { formatTimestamp } from "../../../lib/formatTimestamp";
import { playClick } from "../../../lib/sounds";
import { navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Avatar } from "../../Avatar";
import { Button } from "../../Button";
import { CodeBlock } from "../../CodeBlock";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { InfiniteScroll } from "../../InfiniteScroll";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../../ListFilter";
import { Modal } from "../../Modal";
import { SpreadsheetTable, Td, Th, Thead } from "../../SpreadsheetTable";
import { StatusDot } from "../../StatusDot";
import { Text } from "../../Text";
import { ScrollArea } from "../shared";

const PAGE_SIZE = 50;

const STATUS_META: { id: string; label: string; color: string }[] = [
  { id: "all", label: "All", color: "gray" },
  { id: "success", label: "Success", color: "green" },
  { id: "failed", label: "Failed", color: "red" },
];

const SOURCE_META: { id: string; label: string }[] = [
  { id: "ui", label: "UI" },
  { id: "api", label: "API" },
  { id: "scim", label: "SCIM" },
  { id: "jit", label: "JIT" },
  { id: "mcp", label: "MCP" },
  { id: "federation", label: "Federation" },
  { id: "device", label: "Device" },
  { id: "internal", label: "Internal" },
];

const WINDOW_META: { id: string; label: string; icon: IconName }[] = [
  { id: "all", label: "All time", icon: "circle-dashed" },
  { id: "24h", label: "Last 24 hours", icon: "history" },
  { id: "7d", label: "Last 7 days", icon: "history" },
  { id: "30d", label: "Last 30 days", icon: "history" },
];

const WINDOW_MS: Record<string, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

function menuIcon(name: IconName) {
  return <Icon name={name} size={16} />;
}

function pillIcon(name: IconName) {
  return <Icon name={name} size={12} color="var(--text-secondary)" />;
}

const LogsLayout = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FilterFooter = styled("div")`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  z-index: 1;
  background: var(--surface-primary);
  box-shadow: 0 -0.5px 0 var(--border-alpha);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  min-height: 36px;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const FilterHeaderCell = styled(Th)`
  position: sticky;
  right: 0;
  z-index: 2;
  width: 56px;
  min-width: 56px;
  padding: 0 12px;
  box-shadow:
    inset 0.5px 0 0 var(--border-alpha),
    inset 0 -0.5px 0 var(--border-alpha);
`;

const FilterBodyCell = styled(Td)`
  position: sticky;
  right: 0;
  /* Must stay below the sticky header's stacking level, or this opaque frozen cell covers the filter button on vertical scroll. */
  z-index: 0;
  width: 56px;
  min-width: 56px;
  padding: 0;
  background: var(--surface-primary);
  box-shadow: inset 0.5px 0 0 var(--border-alpha);
`;

// The frozen filter column supplies its own left divider (box-shadow), so drop
// the data column's border-right beside it to avoid a doubled seam.
const AuditTable = styled(SpreadsheetTable)`
  th:nth-last-child(2),
  td:nth-last-child(2) {
    border-right: none;
  }
`;

const EmptyRowCell = styled("td")`
  padding: 48px 24px;
  text-align: center;
`;

const LinkCell = styled(Td)`
  cursor: pointer;
  position: relative;

  & > .link-icon {
    display: none;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    padding-right: 16px;
    padding-left: 24px;
    align-items: center;
    background: linear-gradient(
      to right,
      transparent,
      var(--surface-hover) 40%
    );
  }

  &:hover > .link-icon {
    display: flex;
  }
`;

const ModalContent = styled("div")`
  width: 560px;
  height: 480px;
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const columns = [
  { key: "timestamp", label: "Time", width: 180 },
  { key: "user", label: "Member" },
  { key: "operation", label: "Operation" },
  { key: "source", label: "Source", width: 120 },
  { key: "status", label: "Status", width: 120 },
  { key: "error", label: "Error", width: 120 },
  { key: "inputs", label: "Input", width: 120 },
  { key: "output", label: "Output", width: 120 },
  { key: "context", label: "Request context", width: 120 },
] as const;

export function AuditLogsList() {
  const {
    q,
    status,
    source,
    user,
    window: windowParam,
  } = useCurrentUrl(routes.settings.auditLogs.logs);
  const search = q ?? "";
  const statusFilter = status ?? "all";
  const sourceValues = parseMultiFilter(source);
  const userFilter = user ?? "all";
  const windowFilter = windowParam ?? "all";

  const setStatus = (next: string) =>
    navigate(null, { status: next }, { replace: true });
  const setSource = (next: string) =>
    navigate(null, { source: next }, { replace: true });
  const setUser = (next: string) =>
    navigate(null, { user: next }, { replace: true });
  const setWindow = (next: string) =>
    navigate(null, { window: next }, { replace: true });

  const since = useMemo(() => {
    const ms = WINDOW_MS[windowFilter];
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    return ms ? Date.now() - ms : undefined;
  }, [windowFilter]);

  const [limit, setLimit] = useState(PAGE_SIZE);
  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setLimit(PAGE_SIZE);
  }, [q, status, source, user, windowParam]);

  const [auditLogs, auditLogsResult] = useQuery(
    queries.auditLogs({
      limit,
      searchText: search.trim() || undefined,
      success:
        statusFilter === "success"
          ? true
          : statusFilter === "failed"
            ? false
            : undefined,
      sources: sourceValues.length > 0 ? sourceValues : undefined,
      userId: userFilter === "all" ? undefined : userFilter,
      since,
    })
  );
  const [allUsers] = useQuery(queries.allUsers());
  const [detailModal, setDetailModal] = useState<{
    title: string;
    data: ReadonlyJSONValue | string;
  } | null>(null);

  const usersById = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    for (const u of allUsers) {
      map.set(u.id, { name: u.name, email: u.email });
    }
    return map;
  }, [allUsers]);

  const filters: FilterDef[] = [
    {
      kind: "single",
      key: "status",
      label: "Status",
      icon: <Icon name="circle-dashed" size={16} />,
      options: STATUS_META.map((s) => ({
        id: s.id,
        label: s.label,
        menuLeft: <StatusDot color={s.color} size={12} />,
        pillLeft: <StatusDot color={s.color} size={10} />,
      })),
      value: statusFilter === "all" ? null : statusFilter,
      defaultValue: "all",
      onSelect: setStatus,
      onClear: () => setStatus("all"),
    },
    {
      kind: "multi",
      key: "source",
      label: "Source",
      icon: <Icon name="log-in" size={16} />,
      options: SOURCE_META.map((s) => ({
        id: s.id,
        label: s.label,
      })),
      values: sourceValues,
      onToggle: (id) => setSource(toggleMultiFilter(sourceValues, id)),
    },
    {
      kind: "single",
      key: "user",
      label: "Member",
      icon: <Icon name="circle-user-round" size={16} />,
      options: [
        ...allUsers.map((u) => ({
          id: u.id,
          label: userDisplayName(u),
          menuLeft: <Avatar user={u} size={16} />,
          pillLeft: <Avatar user={u} size={12} />,
        })),
        {
          id: "none",
          label: "No member (system)",
          menuLeft: <Icon name="circle-off" size={16} />,
          pillLeft: (
            <Icon name="circle-off" size={12} color="var(--text-secondary)" />
          ),
        },
      ],
      value: userFilter === "all" ? null : userFilter,
      defaultValue: "all",
      onSelect: setUser,
      onClear: () => setUser("all"),
    },
    {
      kind: "single",
      key: "window",
      label: "Time range",
      icon: <Icon name="clock" size={16} />,
      options: WINDOW_META.map((w) => ({
        id: w.id,
        label: w.label,
        menuLeft: menuIcon(w.icon),
        pillLeft: pillIcon(w.icon),
      })),
      value: windowFilter === "all" ? null : windowFilter,
      defaultValue: "all",
      onSelect: setWindow,
      onClear: () => setWindow("all"),
    },
  ];

  const filtersActive = hasActiveFilters(filters) || search.trim() !== "";
  const showTable = auditLogs.length > 0 || filtersActive;

  return (
    <LogsLayout>
      <ScrollArea>
        {showTable ? (
          <AuditTable>
            <colgroup>
              {columns.map((col) => (
                <col
                  key={col.key}
                  style={"width" in col ? { width: col.width } : undefined}
                />
              ))}
              <col style={{ width: 56 }} />
            </colgroup>
            <Thead>
              <tr>
                {columns.map((col) => (
                  <Th key={col.key}>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {col.label}
                    </Text>
                  </Th>
                ))}
                <FilterHeaderCell>
                  <ListFilter filters={filters} />
                </FilterHeaderCell>
              </tr>
            </Thead>
            <tbody>
              {auditLogs.length === 0 ? (
                auditLogsResult.type === "complete" ? (
                  <tr>
                    <EmptyRowCell colSpan={columns.length + 1}>
                      <Text variant="text-small" color="var(--text-secondary)">
                        No audit logs match your search or filters.
                      </Text>
                    </EmptyRowCell>
                  </tr>
                ) : null
              ) : (
                auditLogs.map((log) => {
                  const logUser = log.userId
                    ? usersById.get(log.userId)
                    : undefined;
                  return (
                    <tr key={log.id}>
                      <Td>
                        <Text variant="text-small">
                          {log.timestamp ? formatTimestamp(log.timestamp) : ""}
                        </Text>
                      </Td>
                      <Td>
                        <Text variant="text-small" ellipsis>
                          {logUser ? userDisplayName(logUser) : log.userId}
                        </Text>
                      </Td>
                      <Td>
                        <Text variant="text-small" ellipsis>
                          {log.operationName}
                        </Text>
                      </Td>
                      <Td>
                        <Text variant="text-small" ellipsis>
                          {log.source}
                        </Text>
                      </Td>
                      <Td>
                        <Text
                          variant={log.success ? "text-small" : "text-small-md"}
                          color={log.success ? undefined : "var(--red-600)"}
                        >
                          {log.success ? "Success" : "Failed"}
                        </Text>
                      </Td>
                      {log.error ? (
                        <LinkCell
                          onMouseDown={() => {
                            playClick();
                            setDetailModal({
                              title: "Error",
                              data: log.error!,
                            });
                          }}
                        >
                          <Text variant="text-small" color="var(--red)">
                            {typeof log.error === "string"
                              ? "1 error"
                              : `${Object.keys(log.error as object).length} field${Object.keys(log.error as object).length === 1 ? "" : "s"}`}
                          </Text>
                          <span className="link-icon">
                            <Icon
                              name="maximize-2"
                              size={12}
                              color="var(--text-secondary)"
                            />
                          </span>
                        </LinkCell>
                      ) : (
                        <Td />
                      )}
                      <LinkCell
                        onMouseDown={() => {
                          playClick();
                          setDetailModal({
                            title: "Inputs",
                            data: log.inputs,
                          });
                        }}
                      >
                        <Text variant="text-small">
                          {typeof log.inputs === "object" && log.inputs !== null
                            ? `${Object.keys(log.inputs).length} field${Object.keys(log.inputs).length === 1 ? "" : "s"}`
                            : "1 field"}
                        </Text>
                        <span className="link-icon">
                          <Icon
                            name="maximize-2"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        </span>
                      </LinkCell>
                      {log.output ? (
                        <LinkCell
                          onMouseDown={() => {
                            playClick();
                            setDetailModal({
                              title: "Output",
                              data: log.output!,
                            });
                          }}
                        >
                          <Text variant="text-small">
                            {typeof log.output === "object"
                              ? `${Object.keys(log.output).length} field${Object.keys(log.output).length === 1 ? "" : "s"}`
                              : "1 field"}
                          </Text>
                          <span className="link-icon">
                            <Icon
                              name="maximize-2"
                              size={12}
                              color="var(--text-secondary)"
                            />
                          </span>
                        </LinkCell>
                      ) : (
                        <Td />
                      )}
                      {log.ipAddress || log.userAgent ? (
                        <LinkCell
                          onMouseDown={() => {
                            playClick();
                            const context: Record<string, string> = {};
                            if (log.ipAddress)
                              context.ipAddress = log.ipAddress;
                            if (log.userAgent)
                              context.userAgent = log.userAgent;
                            setDetailModal({
                              title: "Request context",
                              data: context,
                            });
                          }}
                        >
                          <Text variant="text-small">
                            {log.ipAddress && log.userAgent
                              ? "2 fields"
                              : "1 field"}
                          </Text>
                          <span className="link-icon">
                            <Icon
                              name="maximize-2"
                              size={12}
                              color="var(--text-secondary)"
                            />
                          </span>
                        </LinkCell>
                      ) : (
                        <Td />
                      )}
                      <FilterBodyCell />
                    </tr>
                  );
                })
              )}
            </tbody>
          </AuditTable>
        ) : auditLogsResult.type === "complete" ? (
          <EmptyState
            iconName="logs"
            title="No audit logs"
            description="Team actions appear here."
          />
        ) : null}
        <InfiniteScroll
          hasMore={auditLogs.length >= limit}
          onLoadMore={() => setLimit((prev) => prev + PAGE_SIZE)}
        />
      </ScrollArea>
      {hasActiveFilters(filters) && (
        <FilterFooter>
          <ListFilterPills filters={filters} />
        </FilterFooter>
      )}
      <Modal open={detailModal !== null} onClose={() => setDetailModal(null)}>
        {detailModal && (
          <ModalContent>
            <ModalHeader>
              <Text variant="text-regular-md" ellipsis>
                {detailModal.title}
              </Text>
              <Button
                size="l"
                variant="secondary"
                icon="x"
                onClick={() => setDetailModal(null)}
              />
            </ModalHeader>
            <CodeBlock
              fill
              content={
                typeof detailModal.data === "string"
                  ? detailModal.data
                  : JSON.stringify(detailModal.data, null, 2)
              }
            />
          </ModalContent>
        )}
      </Modal>
    </LogsLayout>
  );
}
